import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { surveysQuery, useCreateSurvey, type Survey } from "../components/surveys/api";
import { Pill } from "../components/ui/Pill";
import { formatDateTime } from "../lib/format";

export const Route = createFileRoute("/_auth/_messaging/surveys/")({
  staticData: { titleKey: "nav.messaging", breadcrumbKey: "breadcrumb.operate", breadcrumbN: 7 },
  component: SurveysIndex,
});

function statusTone(s: Survey["status"]) {
  if (s === "open") return "success" as const;
  if (s === "closed") return "default" as const;
  return "warn" as const;
}

function SurveysIndex() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery(surveysQuery);
  const create = useCreateSurvey();
  const [title, setTitle] = useState("");

  function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    create.mutate(
      { title: title.trim() },
      {
        onSuccess: (s) => {
          setTitle("");
          void navigate({ to: "/surveys/$id", params: { id: s.id } });
        },
      },
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <form onSubmit={onCreate} style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("survey.newPlaceholder")}
          style={{
            font: "inherit",
            fontSize: 14,
            padding: "8px 12px",
            border: "1px solid var(--line)",
            borderRadius: 8,
            background: "var(--bg)",
            color: "var(--fg)",
            minWidth: 280,
          }}
        />
        <button
          type="submit"
          disabled={!title.trim() || create.isPending}
          style={{
            font: "inherit",
            fontSize: 14,
            padding: "8px 16px",
            border: "1px solid var(--fg)",
            borderRadius: 8,
            background: "var(--fg)",
            color: "var(--bg)",
            cursor: "pointer",
          }}
        >
          {t("survey.create")}
        </button>
      </form>

      {isLoading && <span style={{ color: "var(--fg-mute)" }}>…</span>}
      {data?.items.length === 0 && (
        <span style={{ color: "var(--fg-mute)", fontSize: 13 }}>{t("survey.empty")}</span>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(data?.items ?? []).map((s) => (
          <Link
            key={s.id}
            to="/surveys/$id"
            params={{ id: s.id }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "14px 16px",
              border: "1px solid var(--line)",
              borderRadius: "var(--radius)",
              textDecoration: "none",
              color: "var(--fg)",
            }}
          >
            <span style={{ fontWeight: 500 }}>{s.title}</span>
            <Pill tone={statusTone(s.status)}>{t(`survey.status.${s.status}`)}</Pill>
            <span style={{ fontSize: 12.5, color: "var(--fg-mute)" }}>
              {t("survey.answeredOf", { responded: s.responded, invited: s.invited })}
            </span>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--fg-faint)" }}>
              {formatDateTime(s.created_at)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
